import { useState } from "react";
import { motion } from "framer-motion";
import { Github, Linkedin, Mail, ArrowUpRight, MessageSquare } from "lucide-react";
import { ContactForm } from "./ContactForm";
import { useLanguage } from "../i18n/LanguageProvider";

const socialLinks = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/in/alper-kirmizi",
    icon: Linkedin,
    description: "Professional network",
  },
  {
    label: "GitHub",
    href: "https://github.com/alperkirmizi",
    icon: Github,
    description: "Open source & code",
  },
  {
    label: "Email",
    href: "mailto:alperkirmizi99@gmail.com",
    icon: Mail,
    description: "Get in touch",
  },
];

export const Contact = () => {
  const { t } = useLanguage();
  const [showContactForm, setShowContactForm] = useState(false);

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-sm tracking-widest uppercase text-zinc-500 font-medium mb-4"
        >
          {t("contact.label")}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-3xl md:text-4xl font-semibold tracking-tight text-white/90 mb-6"
        >
          {t("contact.sectionTitle")}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-base text-zinc-500 max-w-xl mb-10 leading-relaxed"
        >
          {t("contact.intro")}
        </motion.p>

        {/* Leave a Message Button */}
        <motion.button
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
          onClick={() => setShowContactForm(true)}
          data-testid="leave-message-btn"
          className="group inline-flex items-center gap-3 px-6 py-3.5 bg-white text-black hover:bg-zinc-100 rounded-full font-medium text-sm transition-all duration-300 mb-12"
        >
          <MessageSquare size={18} strokeWidth={1.5} />
          {t("contact.leaveMessage")}
          <ArrowUpRight size={16} className="opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-200" />
        </motion.button>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {socialLinks.map((link, i) => (
            <motion.a
              key={link.label}
              href={link.href}
              target={link.label !== "Email" ? "_blank" : undefined}
              rel={link.label !== "Email" ? "noopener noreferrer" : undefined}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              data-testid={`contact-${link.label.toLowerCase()}`}
              className="group flex items-center justify-between p-6 bg-[#0F0F10] border border-white/5 rounded-2xl hover:border-white/10 transition-colors duration-300 card-shine border-glow"
            >
              <div className="flex items-center gap-4">
                <div className="p-2.5 rounded-xl bg-white/5 group-hover:bg-white/10 transition-colors duration-200">
                  <link.icon size={20} className="text-zinc-300" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">{link.label}</p>
                  <p className="text-xs text-zinc-500">{t(`contact.${link.label.toLowerCase()}Description`, link.description)}</p>
                </div>
              </div>
              <ArrowUpRight
                size={16}
                className="text-zinc-600 group-hover:text-zinc-400 shrink-0 transition-colors duration-200"
                strokeWidth={1.5}
              />
            </motion.a>
          ))}
        </div>

        {/* Contact Form Modal */}
        <ContactForm isOpen={showContactForm} onClose={() => setShowContactForm(false)} />
      </div>
    </section>
  );
};
